import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import { usePrefersReducedMotion } from './motion';

/** Search params that do not change which rows are listed, or change them as the user types. */
const IGNORED = new Set(['q', 'pageSize', 'sort', 'dir']);
/** Height of the sticky top bar plus a little air, so the results start just below it. */
const OFFSET = 56 + 12;

/**
 * Brings a list page's results into view when its filters change (a category tile, a status chip, the next
 * page) and on arrival with filters already in the link (a dashboard tile). Leaves the page alone when the
 * results already start in the upper part of the screen, so filtering from the toolbar never jumps.
 */
export function useScrollToResults<T extends HTMLElement = HTMLDivElement>(ready: boolean) {
  const ref = useRef<T>(null);
  const [sp] = useSearchParams();
  const reduced = usePrefersReducedMotion();
  const signature = [...sp.entries()]
    .filter(([k]) => !IGNORED.has(k))
    .map(([k, v]) => `${k}=${v}`)
    .sort()
    .join('&');
  const pending = useRef(signature !== '');
  const last = useRef(signature);
  if (last.current !== signature) {
    last.current = signature;
    pending.current = true;
  }

  useEffect(() => {
    if (!pending.current || !ready) return;
    // After the frame, so the shell's scroll-to-top on navigation has run and the rows have laid out.
    const frame = requestAnimationFrame(() => {
      pending.current = false;
      const el = ref.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      if (top >= OFFSET - 4 && top <= window.innerHeight * 0.35) return;
      window.scrollTo({ top: window.scrollY + top - OFFSET, behavior: reduced ? 'auto' : 'smooth' });
    });
    return () => cancelAnimationFrame(frame);
  }, [signature, ready, reduced]);

  return ref;
}
